import { firstLoadPending } from '@/api/firstLoadPending';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { OrgLogo, Spinner, Text } from '@/components/primitives';
import { Icons, vars } from '@/theme';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { updateCurrentUser } from '@/auth/authData';
import {
  contextKeys, useOrganizations, useNetworks, useSwitchContext,
} from '@/features/context/hooks/useContextSwitching';
import { ContextCreateMenu } from '@/features/staff/components/ContextCreateMenu';
import { ErrorReference } from '@/components/errors';
import { useDebounce } from '@/hooks/useDebounce';
import { SEARCH_DEBOUNCE_MS, paths } from '@/constants';
import { identifyRefusal } from '@/api/knownRefusals';
import { isGloballyRouted } from '@/api/failureClass';
import { testIds } from '@/test/testIds';

export interface ContextSwitcherListProps {
  /** Called after a successful switch (e.g. to close the host dialog). */
  onSwitched?: () => void;
  /** Show the search box. Default true; hide for tiny lists. */
  showSearch?: boolean;
}

const ids = testIds.contextSwitcher;

/** One row in the unified list — orgs and networks flattened into one list. */
type SwitcherEntry =
  | { kind: 'org'; id: number; label: string; sub: string; logoUrl: string | null; isCurrent: boolean }
  | { kind: 'network'; id: number; label: string; sub: string; isCurrent: boolean };

/**
 * ContextSwitcherList — searchable list of the orgs/networks the user can work
 * in. Clicking a row switches the active context immediately. The current
 * context is marked and non-actionable. Lifted from the retired ⌘K
 * ContextPalette; now the body of the Organization/Network dialog's switcher.
 *
 * Only render this when the user can actually switch (`canSwitchContext`).
 */
export function ContextSwitcherList({ onSwitched, showSearch = true }: ContextSwitcherListProps) {
  const { currentContext } = useCurrentUser();
  const navigate = useNavigate();

  // After creating a brand-new context we land on /manage for it (not the
  // default home), and close the host dialog. ContextCreateMenu is staff-gated
  // internally, so non-staff users never see the control.
  const handleCreated = () => {
    void navigate(paths.manage);
    onSwitched?.();
  };

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  const {
    data: organizations = [], isPending: orgsLoadingPending, fetchStatus: orgsLoadingFetchStatus, isError: orgsError, error: orgsErrorObj,
  } = useOrganizations({
    enabled: true,
    search: debouncedSearch,
  });
  // Not TanStack's `isLoading`: a first load paused offline is still waiting (#1569, #1561-g).
  const orgsLoading = firstLoadPending({ isPending: orgsLoadingPending, fetchStatus: orgsLoadingFetchStatus });
  const {
    data: networks = [], isPending: networksLoadingPending, fetchStatus: networksLoadingFetchStatus, isError: networksError, error: networksErrorObj,
  } = useNetworks({
    enabled: true,
    search: debouncedSearch,
  });
  // Not TanStack's `isLoading`: a first load paused offline is still waiting (#1569, #1561-g).
  const networksLoading = firstLoadPending({ isPending: networksLoadingPending, fetchStatus: networksLoadingFetchStatus });

  const switchContext = useSwitchContext({ reportKnownRefusal: true });
  const queryClient = useQueryClient();
  // Set only for a `context_not_member` refusal (#1685-b, Q-553): the clicked row's label.
  const [refusedLabel, setRefusedLabel] = useState<string | null>(null);

  const currentOrgId = currentContext?.type === 'org' ? currentContext.organization?.id ?? null : null;
  const currentNetworkId = currentContext?.type === 'network' ? currentContext.network?.id ?? null : null;

  const entries = useMemo<SwitcherEntry[]>(() => {
    // Defensive: the API may return a non-array on error.
    const orgs = Array.isArray(organizations) ? organizations : [];
    const nets = Array.isArray(networks) ? networks : [];
    const orgEntries: SwitcherEntry[] = orgs.map((o) => ({
      kind: 'org',
      id: o.id,
      label: o.name || o.identifier,
      sub: o.level ?? '',
      logoUrl: o.logo?.url ?? null,
      isCurrent: o.id === currentOrgId,
    }));
    const netEntries: SwitcherEntry[] = nets.map((n) => {
      const orgs = `${n.organizationCount} organization${n.organizationCount !== 1 ? 's' : ''}`;
      return {
        kind: 'network' as const,
        id: n.id,
        label: n.name || n.identifier,
        sub: n.role ? `${orgs} · ${n.role}` : orgs,
        isCurrent: n.id === currentNetworkId,
      };
    });
    return [...orgEntries, ...netEntries];
  }, [organizations, networks, currentOrgId, currentNetworkId]);

  const isLoading = orgsLoading || networksLoading;
  const isError = orgsError || networksError;
  const isSwitching = switchContext.isPending;

  const handleSelect = async (entry: SwitcherEntry) => {
    if (isSwitching) return;
    if (entry.isCurrent) {
      onSwitched?.();
      return;
    }
    setRefusedLabel(null);
    try {
      const result = await switchContext.mutateAsync({ type: entry.kind, id: entry.id });
      updateCurrentUser(result);
      onSwitched?.();
    } catch (err) {
      // Surfaced via switchContext.isError below; keep the list open to retry.
      if (identifyRefusal(err)?.kind === 'context_not_member') {
        // The access is gone: say so, and reload the lists so the row drops out.
        setRefusedLabel(entry.label);
        void queryClient.invalidateQueries({ queryKey: contextKeys.organizations() });
        void queryClient.invalidateQueries({ queryKey: contextKeys.networks() });
      }
    }
  };

  const firstNetworkIndex = entries.findIndex((entry) => entry.kind === 'network');

  // A 5xx or transport failure is shown once by the central routing (the dialog's band, or the
  // global banner on the page); only a failure it does not show (an unmatched 4xx) is said here.
  const showSwitchError = switchContext.isError
    && (refusedLabel !== null || !isGloballyRouted(switchContext.error));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      {showSearch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexShrink: 0 }}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px',
              border: `1px solid ${vars.color.surface.border}`,
              borderRadius: 0,
            }}
          >
            <Icons.search style={{ fontSize: 18, color: vars.color.neutral.subtle }} />
            <input
              type="text"
              aria-label="Search organizations and networks"
              data-testid={ids.searchInput}
              value={searchQuery}
              placeholder="Search organizations and networks…"
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: '0.95rem',
                padding: '6px 0',
                color: vars.color.neutral.darker,
              }}
            />
          </div>
          {/* Staff-only create control, on the same line as the search bar. */}
          <ContextCreateMenu onCreated={handleCreated} />
        </div>
      )}

      <div
        data-testid={ids.scrollArea}
        style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
      >
        {isLoading ? (
          <div data-testid={ids.loadingState} style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
            <Spinner size={28} />
          </div>
        ) : isError ? (
          <div data-testid={ids.errorState} style={{ padding: '32px 0', textAlign: 'center' }}>
            <Text color="secondary">Couldn&apos;t load contexts. Please try again.</Text>
            <ErrorReference error={orgsErrorObj ?? networksErrorObj} />
          </div>
        ) : entries.length === 0 ? (
          <div data-testid={ids.emptyState} style={{ padding: '32px 0', textAlign: 'center' }}>
            <Text color="secondary">
              {searchQuery ? 'No matching organizations or networks' : 'No contexts available'}
            </Text>
          </div>
        ) : (
          <ul
            aria-label="Contexts"
            data-testid={ids.list}
            style={{ listStyle: 'none', margin: 0, padding: 0 }}
          >
            {entries.map((entry, index) => {
              const showOrgHeader = index === 0 && entry.kind === 'org';
              const showNetworkHeader = index === firstNetworkIndex && entry.kind === 'network';
              return (
                <li key={`${entry.kind}-${entry.id}`} style={{ margin: 0 }}>
                  {showOrgHeader && <GroupHeader label="Organizations" testId={ids.orgGroup} />}
                  {showNetworkHeader && <GroupHeader label="Networks" testId={ids.networkGroup} />}
                  <button
                    type="button"
                    aria-current={entry.isCurrent || undefined}
                    disabled={isSwitching}
                    data-testid={ids.item(entry.kind, entry.id)}
                    onClick={() => void handleSelect(entry)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: 0,
                      border: 'none',
                      background: 'transparent',
                      textAlign: 'left',
                      cursor: isSwitching ? 'default' : 'pointer',
                      font: 'inherit',
                    }}
                  >
                    {entry.kind === 'org' ? (
                      <OrgLogo src={entry.logoUrl} name={entry.label} height={22} />
                    ) : (
                      <Icons.network style={{ fontSize: 20, color: vars.color.neutral.subtle }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontWeight: entry.isCurrent ? 600 : 400 }}>{entry.label}</Text>
                      <Text variant="caption" color="secondary">{entry.sub}</Text>
                    </div>
                    {entry.isCurrent && (
                      <Icons.check style={{ fontSize: 18, color: vars.color.success.base }} />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showSwitchError && (
        <div data-testid={ids.switchError} style={{ marginTop: 8, textAlign: 'center', flexShrink: 0 }}>
          {refusedLabel !== null ? (
            <Text color="error">
              You no longer have access to {refusedLabel}.
            </Text>
          ) : (
            <>
              <Text color="error">
                Failed to switch context. Please try again.
              </Text>
              <ErrorReference error={switchContext.error} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function GroupHeader({ label, testId }: { label: string; testId: string }) {
  return (
    <Text
      variant="smallLabel"
      color="secondary"
      data-testid={testId}
      style={{ display: 'block', padding: '8px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
    >
      {label}
    </Text>
  );
}
